<template>
  <div class="app-content">
    <div class="app-content-body fade-in-up content-transition">
      <div class="chat-admin-page">
        <div class="admin-header">
          <div>
            <h1>Painel de Chat - MASTER</h1>
            <p class="admin-subtitle">Acompanhe conversas, mídias e histórico em tempo real.</p>
          </div>

          <div class="admin-stats">
            <div class="stat-card">
              <div class="stat-value">{{ totalRooms }}</div>
              <div class="stat-label">Conversas</div>
            </div>
            <div class="stat-card">
              <div class="stat-value">{{ totalMessages }}</div>
              <div class="stat-label">Mensagens</div>
            </div>
          </div>
        </div>

        <div class="admin-body" :class="{ 'room-selected': !!selectedRoom }">
          <div class="rooms-panel">
            <div class="panel-header">
              <h2>Conversas</h2>
              <input
                v-model="searchQuery"
                @input="searchRooms"
                placeholder="Buscar conversa/usuário..."
                class="search-input"
              />
            </div>

            <div v-if="loading" class="loading">Carregando...</div>

            <div v-else class="rooms-list">
              <div
                v-for="room in rooms"
                :key="room.id"
                @click="selectRoom(room)"
                class="room-card"
                :class="{ active: selectedRoom && selectedRoom.id === room.id }"
              >
                <div class="room-avatar">
                  {{ getRoomInitial(room) }}
                </div>

                <div class="room-info">
                  <div class="room-top">
                    <div class="room-name">{{ room.name || ('Conversa #' + room.id) }}</div>
                  </div>

                  <div class="room-users">
                    {{ getRoomUsers(room) }}
                  </div>

                  <div class="room-last">
                    {{ getRoomLastMessage(room) }}
                  </div>
                </div>
              </div>

              <div v-if="rooms.length === 0" class="empty">Nenhuma conversa</div>
            </div>
          </div>

          <div class="messages-panel">
            <div v-if="!selectedRoom" class="empty-state">
              <div class="empty-state-inner">
                <div class="empty-icon">💬</div>
                <p>Selecione uma conversa</p>
              </div>
            </div>

            <div v-else class="messages-view">
              <div class="messages-header">
                <div class="messages-header-left">
                  <button @click="selectedRoom = null" class="back-btn">←</button>

                  <div>
                    <h3>{{ selectedRoom.name || ('Conversa #' + selectedRoom.id) }}</h3>
                    <div class="selected-room-users">
                      {{ getRoomUsers(selectedRoom) }}
                    </div>
                  </div>
                </div>

                <button @click="exportMessages" class="export-btn">Exportar</button>
              </div>

              <div class="messages-container" ref="messagesContainer">
                <div v-if="loadingMessages" class="loading">Carregando...</div>

                <div v-else>
                  <div v-for="msg in messages" :key="msg.id" class="message-item">
                    <div class="message-header">
                      <div class="sender-avatar">
                        {{ getSenderInitial(msg) }}
                      </div>

                      <div class="sender-info">
                        <strong>{{ getSenderName(msg) }}</strong>
                        <span class="message-time">{{ formatDateTime(msg.timestamp) }}</span>
                      </div>
                    </div>

                    <div class="message-body">
                      <div v-if="hasFiles(msg)" class="files-wrap">
                        <div
                          v-for="file in msg.files"
                          :key="file.id || file.url"
                          class="file-item"
                        >
                          <a
                            v-if="isImage(file)"
                            :href="file.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="image-link"
                          >
                            <img
                              :src="file.url"
                              :alt="file.name || 'imagem'"
                              class="message-image"
                            />
                          </a>

                          <a
                            v-else
                            :href="file.url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="file-link"
                          >
                            <div class="file-icon">📎</div>
                            <div class="file-info">
                              <div class="file-name">{{ file.name || 'arquivo' }}</div>
                              <div class="file-size">{{ prettySize(file.size) }}</div>
                            </div>
                          </a>
                        </div>
                      </div>

                      <div
                        v-if="getMessageText(msg)"
                        class="message-content"
                      >
                        {{ getMessageText(msg) }}
                      </div>
                    </div>
                  </div>

                  <div v-if="messages.length === 0" class="empty">Nenhuma mensagem</div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import AclCheckMixins from '../../_default/components/mixins/AclCheckMixins.vue'

export default {
  name: 'ChatAdmin',
  mixins: [AclCheckMixins],
  data: function () {
    return {
      rooms: [],
      messages: [],
      selectedRoom: null,
      searchQuery: '',
      loading: false,
      loadingMessages: false,
      searchTimeout: null,
      totalRooms: 0,
      totalMessages: 0,
      roomRefreshInterval: null,
      socketBound: false
    }
  },
  mounted: function () {
    var podeAcessar = this.possuiACL('acesso_chat_master') || this.isAdministradorExclusivo

    if (!podeAcessar) {
      alert('Você não tem permissão para acessar o painel de chat (MASTER).')
      this.$router.replace({ path: '/' })
      return
    }

    this.initRealtime()
    this.loadRooms()
    this.startPollingFallback()
  },
  beforeDestroy: function () {
    this.stopPollingFallback()

    if (this.$chat && this.$chat.socket && this.$chat.socket.socket && this.socketBound) {
      this.$chat.socket.socket.off('chat:new-message', this.onSocketNewMessage)
      this.socketBound = false
    }
  },
  methods: {
    initRealtime: function () {
      var self = this
      var userId = this.$store &&
      this.$store.state &&
      this.$store.state.auth &&
      this.$store.state.auth.user
        ? this.$store.state.auth.user.id
        : null

      if (!this.$chat || !this.$chat.socket || !userId) return

      if (!this.$chat.socket.socket || !this.$chat.socket.connected) {
        this.$chat.socket.connect(userId)
      }

      if (this.$chat.socket.socket && !this.socketBound) {
        this.$chat.socket.socket.on('chat:new-message', this.onSocketNewMessage)
        this.socketBound = true
      }

      setTimeout(function () {
        self.joinAllRooms()
      }, 600)
    },

    startPollingFallback: function () {
      var self = this

      this.stopPollingFallback()

      this.roomRefreshInterval = setInterval(function () {
        self.loadRooms(true)

        if (self.selectedRoom && self.selectedRoom.id) {
          self.loadMessages(self.selectedRoom.id, true)
        }
      }, 15000)
    },

    stopPollingFallback: function () {
      if (this.roomRefreshInterval) {
        clearInterval(this.roomRefreshInterval)
        this.roomRefreshInterval = null
      }
    },

    joinAllRooms: function () {
      if (!this.$chat || !this.$chat.socket) return

        ;(this.rooms || []).forEach(function (room) {
          if (room && room.id) {
            try {
              this.$chat.socket.joinRoom(room.id)
            } catch (e) {
              console.error('Erro ao entrar na sala', room.id, e)
            }
          }
        }, this)
    },

    onSocketNewMessage: function (message) {
      if (!message || !message.roomId) return

      var roomId = String(message.roomId)
      var selectedId = this.selectedRoom && this.selectedRoom.id ? String(this.selectedRoom.id) : null

      this.upsertRoomLastMessage(message)

      if (selectedId && selectedId === roomId) {
        this.upsertMessage(message)

        this.totalMessages = this.messages.length

        this.$nextTick(function () {
          this.scrollToBottom()
        })
      }
    },

    loadRooms: function (silent) {
      var self = this

      if (!silent) this.loading = true

      this.$chat.api.getAdminRooms(this.searchQuery)
        .then(function (data) {
          self.rooms = self.sortRoomsByRecent(data || [])
          self.totalRooms = self.rooms.length

          self.$nextTick(function () {
            self.joinAllRooms()
          })

          if (!silent) self.loading = false
        })
        .catch(function (error) {
          console.error('Erro ao carregar salas:', error)
          if (!silent) self.loading = false
          self.rooms = []

          if (error && error.status === 403) {
            alert('Você não tem permissão para acessar o painel admin')
          } else if (error && error.status === 404) {
            console.error('Endpoint /api/chat/admin/rooms não encontrado')
          }
        })
    },

    searchRooms: function () {
      var self = this
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(function () {
        self.loadRooms()
      }, 400)
    },

    selectRoom: function (room) {
      this.selectedRoom = room

      if (this.$chat && this.$chat.socket) {
        try {
          this.$chat.socket.joinRoom(room.id)
        } catch (e) {
          console.error('Erro ao entrar na sala selecionada', e)
        }
      }

      this.loadMessages(room.id)
    },

    loadMessages: function (roomId, silent) {
      var self = this

      if (!silent) this.loadingMessages = true

      this.$chat.api.getAdminMessages(roomId, { limit: 100 })
        .then(function (data) {
          self.messages = self.sortMessagesAsc(data || [])
          self.totalMessages = self.messages.length

          if (!silent) self.loadingMessages = false

          self.$nextTick(function () {
            self.scrollToBottom()
          })
        })
        .catch(function (error) {
          console.error('Erro ao carregar mensagens:', error)
          if (!silent) self.loadingMessages = false
          self.messages = []
        })
    },

    sortRoomsByRecent: function (rooms) {
      return (rooms || []).slice().sort(function (a, b) {
        var ta = this.getRoomSortTime(a)
        var tb = this.getRoomSortTime(b)
        return tb - ta
      }.bind(this))
    },

    sortMessagesAsc: function (messages) {
      return (messages || []).slice().sort(function (a, b) {
        var ta = this.getMessageTime(a)
        var tb = this.getMessageTime(b)
        return ta - tb
      }.bind(this))
    },

    getRoomSortTime: function (room) {
      if (!room) return 0

      if (room.lastMessage && room.lastMessage.timestamp) {
        return new Date(room.lastMessage.timestamp).getTime() || 0
      }

      if (room.lastMessageTime) {
        return new Date(room.lastMessageTime).getTime() || 0
      }

      if (room.updatedAt) {
        return new Date(room.updatedAt).getTime() || 0
      }

      if (room.createdAt) {
        return new Date(room.createdAt).getTime() || 0
      }

      return 0
    },

    getMessageTime: function (msg) {
      if (!msg || !msg.timestamp) return 0
      return new Date(msg.timestamp).getTime() || 0
    },

    upsertRoomLastMessage: function (message) {
      var roomId = String(message.roomId)
      var idx = this.rooms.findIndex(function (room) {
        return String(room.id) === roomId
      })

      if (idx === -1) {
        this.loadRooms(true)
        return
      }

      var room = Object.assign({}, this.rooms[idx], {
        lastMessage: message,
        lastMessageTime: message.timestamp || null
      })

      this.rooms.splice(idx, 1)
      this.rooms.unshift(room)

      if (this.selectedRoom && String(this.selectedRoom.id) === roomId) {
        this.selectedRoom = room
      }
    },

    upsertMessage: function (message) {
      var id = message && message.id ? String(message.id) : null
      if (!id) {
        this.messages.push(message)
        this.messages = this.sortMessagesAsc(this.messages)
        return
      }

      var idx = this.messages.findIndex(function (msg) {
        return msg && msg.id && String(msg.id) === id
      })

      if (idx >= 0) {
        this.$set(this.messages, idx, Object.assign({}, this.messages[idx], message))
      } else {
        this.messages.push(message)
      }

      this.messages = this.sortMessagesAsc(this.messages)
    },

    scrollToBottom: function () {
      var container = this.$refs.messagesContainer
      if (!container) return

      setTimeout(function () {
        container.scrollTop = container.scrollHeight
      }, 30)
    },

    getRoomInitial: function (room) {
      if (room && room.name && room.name.length) return room.name.charAt(0).toUpperCase()
      return '?'
    },

    getRoomUsers: function (room) {
      if (!room || !room.users || !room.users.length) return 'Sem participantes'
      return room.users.map(function (u) {
        return u.nome
      }).join(', ')
    },

    getRoomLastMessage: function (room) {
      if (!room || !room.lastMessage) return 'Sem mensagens'

      if (room.lastMessage.files && room.lastMessage.files.length) {
        return '[arquivo]'
      }

      if (room.lastMessage.content) return room.lastMessage.content

      return 'Sem mensagens'
    },

    getSenderInitial: function (msg) {
      if (msg && msg.sender && msg.sender.nome && msg.sender.nome.length) {
        return msg.sender.nome.charAt(0).toUpperCase()
      }
      return '?'
    },

    getSenderName: function (msg) {
      if (msg && msg.sender && msg.sender.nome) return msg.sender.nome
      return '---'
    },

    hasFiles: function (msg) {
      return !!(msg && msg.files && msg.files.length)
    },

    getMessageText: function (msg) {
      var txt = msg && msg.content ? String(msg.content) : ''

      if (this.hasFiles(msg)) {
        if (txt === '[arquivo]' || txt === '[file]' || txt === '[anexo]') {
          return ''
        }
      }

      return txt
    },

    isImage: function (file) {
      var mime = ''

      if (file && file.mimetype) mime = String(file.mimetype)
      else if (file && file.mimeType) mime = String(file.mimeType)
      else if (file && file.mime) mime = String(file.mime)

      if (/^image\//.test(mime)) return true

      var url = file && file.url ? String(file.url).toLowerCase() : ''
      return /\.(png|jpg|jpeg|gif|webp|bmp|svg)(\?.*)?$/.test(url)
    },

    prettySize: function (bytes) {
      var n = Number(bytes || 0)

      if (!n) return ''
      if (n < 1024) return n + ' B'
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB'
      return (n / (1024 * 1024)).toFixed(1) + ' MB'
    },

    exportMessages: function () {
      if (!this.selectedRoom) return

      var text = 'CONVERSA: ' + (this.selectedRoom.name || ('Conversa #' + this.selectedRoom.id)) + '\n'
      text += 'DATA: ' + new Date().toLocaleString('pt-BR') + '\n\n'

      this.messages.forEach(function (msg) {
        text += '[' + new Date(msg.timestamp).toLocaleString('pt-BR') + '] '
        text += (msg.sender ? msg.sender.nome : '---') + ': '

        if (msg.files && msg.files.length) {
          text += '[arquivo] '
        }

        text += (msg.content || '') + '\n'
      })

      var blob = new Blob([text], { type: 'text/plain' })
      var url = URL.createObjectURL(blob)
      var a = document.createElement('a')
      a.href = url
      a.download = 'chat-' + this.selectedRoom.id + '.txt'
      a.click()
      URL.revokeObjectURL(url)
    },

    formatDateTime: function (dateString) {
      if (!dateString) return '--/--/---- --:--'
      return new Date(dateString).toLocaleString('pt-BR')
    }
  }
}
</script>

<style scoped>
.chat-admin-page {
  padding: 16px;
}

.admin-header {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 20px 24px;
  border-radius: 10px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.admin-header h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
}

.admin-subtitle {
  margin: 6px 0 0 0;
  opacity: .9;
  font-size: 13px;
}

.admin-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.stat-card {
  min-width: 110px;
  background: rgba(255,255,255,0.18);
  padding: 12px 16px;
  border-radius: 8px;
  text-align: center;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  margin-top: 6px;
  font-size: 12px;
  opacity: .95;
}

.admin-body {
  display: flex;
  gap: 16px;
  height: calc(100vh - 255px);
  min-height: 520px;
}

.rooms-panel,
.messages-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.messages-view {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.rooms-panel {
  width: 360px;
  display: flex;
  flex-direction: column;
}

.messages-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.panel-header,
.messages-header {
  padding: 14px 16px;
  border-bottom: 1px solid #edf1f5;
  background: #fafbfd;
}

.panel-header h2,
.messages-header h3 {
  margin: 0;
}

.search-input {
  width: 100%;
  margin-top: 10px;
  border: 1px solid #d9e1ea;
  border-radius: 8px;
  outline: none;
  padding: 10px 12px;
  background: #fff;
}

.rooms-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.room-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #f3f5f7;
  cursor: pointer;
  transition: background .15s ease;
}

.room-card:hover {
  background: #fafcff;
}

.room-card.active {
  background: #f1f5ff;
  border-left: 4px solid #667eea;
  padding-left: 12px;
}

.room-avatar,
.sender-avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.room-info {
  flex: 1;
  min-width: 0;
}

.room-top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.room-name {
  font-weight: 700;
  color: #2d3748;
  line-height: 1.2;
}

.room-users {
  font-size: 12px;
  color: #718096;
  margin-top: 4px;
  line-height: 1.4;
}

.room-last {
  font-size: 13px;
  color: #4a5568;
  margin-top: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.messages-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.messages-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.selected-room-users {
  margin-top: 4px;
  font-size: 12px;
  color: #718096;
}

.back-btn,
.export-btn {
  border: 0;
  cursor: pointer;
  border-radius: 8px;
  transition: .15s ease;
}

.back-btn {
  width: 36px;
  height: 36px;
  background: #edf2f7;
  color: #2d3748;
  font-size: 18px;
}

.back-btn:hover {
  background: #e2e8f0;
}

.export-btn {
  background: #667eea;
  color: #fff;
  padding: 9px 14px;
  font-weight: 600;
}

.export-btn:hover {
  background: #5a67d8;
}

.messages-container {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px;
  background: #f8fafc;
}

.message-item {
  background: #fff;
  border: 1px solid #ebf0f5;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.03);
}

.message-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.sender-info {
  display: flex;
  flex-direction: column;
}

.message-time {
  font-size: 12px;
  color: #718096;
  margin-top: 2px;
}

.message-body {
  padding-left: 52px;
}

.message-content {
  font-size: 14px;
  color: #2d3748;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.files-wrap {
  display: grid;
  gap: 10px;
  margin-bottom: 10px;
}

.image-link {
  display: inline-block;
  max-width: 320px;
}

.message-image {
  display: block;
  max-width: 100%;
  width: auto;
  max-height: 320px;
  border-radius: 10px;
  border: 1px solid #e5eaf0;
  background: #fff;
}

.file-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #2d3748;
  background: #f8fafc;
  border: 1px solid #e7edf3;
  border-radius: 10px;
  padding: 12px;
  max-width: 420px;
}

.file-link:hover {
  background: #f1f5f9;
}

.file-icon {
  font-size: 22px;
  line-height: 1;
}

.file-info {
  min-width: 0;
}

.file-name {
  font-weight: 600;
  word-break: break-word;
}

.file-size {
  margin-top: 4px;
  font-size: 12px;
  color: #718096;
}

.loading,
.empty,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #718096;
}

.loading,
.empty {
  padding: 30px 16px;
}

.empty-state {
  flex: 1;
  background: #fff;
}

.empty-state-inner {
  text-align: center;
}

.empty-icon {
  font-size: 34px;
  margin-bottom: 10px;
}

@media (max-width: 991px) {
  .admin-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .admin-body {
    height: auto;
    min-height: 0;
    flex-direction: column;
  }

  .rooms-panel {
    width: 100%;
    min-height: 280px;
  }

  .messages-panel {
    min-height: 420px;
  }

  .message-body {
    padding-left: 0;
  }
}
</style>



// WEBPACK FOOTER //
// ChatAdmin.vue?57e80654